import React, { useEffect, useRef } from "react";

/**
 * Contextual-label cursor — a tiny dot trailed by an editorial verb label
 * that reads the action under the pointer.
 *
 *   free    — small dot, no label
 *   labeled — dot grows slightly, label fades in with the action's verb
 *   text    — dot becomes an I-beam over text fields / reading areas
 *   none    — hidden (drag zones; suspended while overlays are open)
 *
 * The label is resolved per pointerover:
 *   1. An explicit `data-cursor-label="VERB"` on the element (or any ancestor)
 *      wins — this is how we give Lab tiles "EXPLORE ✦", the palette "JUMP ⌘",
 *      Ask Sam "ASK ↳", etc.
 *   2. Otherwise, default by element type:
 *        <a> external → "VISIT ↗"
 *        <a> internal → "OPEN →"
 *        <button>     → "TAP"
 *   3. Anything else gets no label — the dot stays in its free state.
 *
 * Motion: the dot follows the pointer 1:1 (instant); the label lags slightly
 * for fluidity. mix-blend-mode keeps the dot visible on any background.
 * Hidden entirely on touch / coarse-pointer devices.
 */
export default function CustomCursor() {
  const dotRef   = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    if (coarse) return;

    document.documentElement.classList.add("has-custom-cursor");

    const LABEL_EASE = 0.22;          // label lag ratio (per rAF tick)
    const LABEL_OFFSET_X = 18;        // label sits down-right of the pointer
    const LABEL_OFFSET_Y = 14;

    let mx = window.innerWidth  / 2;
    let my = window.innerHeight / 2;
    let lx = mx, ly = my;             // eased position for the label

    let state = "free";               // free | labeled | text | none
    let label = "";
    let raf;

    // ── Label resolution ────────────────────────────────────────────────────
    // Find the nearest "click target" — an <a>, a <button>, role=button, OR
    // an element that opts in via [data-cursor-label]. Walking up from the
    // pointer this way means an inner <a> inside a tile labeled
    // "EXPLORE ✦" still resolves to its own "OPEN →" — because the <a> is
    // the closer match. Explicit data-cursor-label on the chosen target wins
    // over the tag default.
    const labelFor = (target) => {
      const click = target?.closest?.(
        "a, button, [role='button'], [data-cursor-label]"
      );
      if (!click) return "";

      const explicit = click.getAttribute("data-cursor-label");
      if (explicit) return explicit;

      if (click.tagName === "A") {
        const href = click.getAttribute("href") || "";
        if (/^https?:\/\//i.test(href)) {
          try {
            const u = new URL(href);
            if (u.host && u.host !== window.location.host) return "VISIT ↗";
          } catch { /* malformed href falls through to internal */ }
        }
        return "OPEN →";
      }
      if (click.tagName === "BUTTON" || click.getAttribute("role") === "button") {
        return "TAP";
      }
      return "";
    };

    // State resolution honours data-cursor="none"/"text" overrides first, then
    // falls through to "labeled" when there's a label, "free" otherwise.
    const stateFor = (target, lbl) => {
      const dc = target?.closest?.("[data-cursor]")?.getAttribute("data-cursor");
      if (dc === "none") return "none";
      if (dc === "text") return "text";
      return lbl ? "labeled" : "free";
    };

    // ── Events ──────────────────────────────────────────────────────────────
    const onMove = (e) => { mx = e.clientX; my = e.clientY; };

    const onOver = (e) => {
      const nextLabel = labelFor(e.target);
      const nextState = stateFor(e.target, nextLabel);

      if (nextState !== state) {
        state = nextState;
        if (dotRef.current)   dotRef.current.dataset.state   = state;
        if (labelRef.current) labelRef.current.dataset.state = state;
      }
      if (nextLabel !== label) {
        label = nextLabel;
        // Only write the text when we have one. Leaving stale text behind a
        // faded-out label is harmless and avoids a flash of empty padding.
        if (labelRef.current && label) labelRef.current.textContent = label;
      }
    };

    // ── rAF loop — dot snaps to pointer, label eases behind ─────────────────
    const tick = () => {
      const dot = dotRef.current;
      const lbl = labelRef.current;
      if (dot) dot.style.transform = `translate3d(${mx}px, ${my}px, 0)`;

      lx += (mx - lx) * LABEL_EASE;
      ly += (my - ly) * LABEL_EASE;
      if (lbl) {
        lbl.style.transform =
          `translate3d(${lx + LABEL_OFFSET_X}px, ${ly + LABEL_OFFSET_Y}px, 0)`;
      }

      raf = requestAnimationFrame(tick);
    };

    // ── Suspend / resume — overlays hand the cursor back to the OS ──────────
    const onSuspend = () => {
      document.documentElement.classList.remove("has-custom-cursor");
      if (dotRef.current)   dotRef.current.style.display   = "none";
      if (labelRef.current) labelRef.current.style.display = "none";
    };
    const onResume = () => {
      document.documentElement.classList.add("has-custom-cursor");
      if (dotRef.current)   dotRef.current.style.display   = "";
      if (labelRef.current) labelRef.current.style.display = "";
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerover", onOver, { passive: true });
    window.addEventListener("erpik:cursor-suspend", onSuspend);
    window.addEventListener("erpik:cursor-resume",  onResume);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerover", onOver);
      window.removeEventListener("erpik:cursor-suspend", onSuspend);
      window.removeEventListener("erpik:cursor-resume",  onResume);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, []);

  // Don't mount on touch / coarse-pointer devices.
  if (typeof window !== "undefined" &&
      window.matchMedia?.("(pointer: coarse)").matches) {
    return null;
  }

  return (
    <>
      <div
        ref={dotRef}
        className="cursor-dot"
        data-state="free"
        data-testid="custom-cursor"
        aria-hidden="true"
      />
      <div
        ref={labelRef}
        className="cursor-label"
        data-state="free"
        aria-hidden="true"
      />
    </>
  );
}
